import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_from: '',
    email_templates: {}
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Einstellungen erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateTemplate = (key, value) => {
    setSettings(prev => ({
      ...prev,
      email_templates: {
        ...prev.email_templates,
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg text-slate-600">Einstellungen werden geladen...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="admin-settings-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/bess/dashboard')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Zurück zum Dashboard
          </Button>
          <h1 className="text-4xl font-bold text-slate-900">System-Einstellungen</h1>
          <p className="text-slate-600 mt-2">E-Mail-Konfiguration und automatisierte Texte verwalten</p>
        </div>

        <div className="space-y-6">
          {/* Email Configuration */}
          <div className="card">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">E-Mail-Konfiguration</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="email_from">Absender E-Mail-Adresse</Label>
                <Input
                  id="email_from"
                  type="email"
                  value={settings.email_from}
                  onChange={(e) => setSettings({ ...settings, email_from: e.target.value })}
                  placeholder="noreply@bess-invest.com"
                  data-testid="email-from-input"
                />
                <p className="text-sm text-slate-500 mt-1">
                  Diese E-Mail-Adresse wird als Absender für alle automatisierten E-Mails verwendet
                </p>
              </div>
            </div>
          </div>

          {/* Email Templates */}
          <div className="card">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">E-Mail-Vorlagen</h2>
            <p className="text-slate-600 mb-6">
              Passen Sie die Texte für automatisierte E-Mails an. Verfügbare Platzhalter: {'{link}'}, {'{project_title}'}, {'{amount}'}
            </p>

            <div className="space-y-6">
              <div>
                <Label htmlFor="verification">E-Mail-Verifizierung</Label>
                <Textarea
                  id="verification"
                  value={settings.email_templates?.verification || ''}
                  onChange={(e) => updateTemplate('verification', e.target.value)}
                  rows={3}
                  data-testid="template-verification"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Platzhalter: {'{link}'}
                </p>
              </div>

              <div>
                <Label htmlFor="interest_approved">Interesse genehmigt</Label>
                <Textarea
                  id="interest_approved"
                  value={settings.email_templates?.interest_approved || ''}
                  onChange={(e) => updateTemplate('interest_approved', e.target.value)}
                  rows={3}
                  data-testid="template-interest-approved"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Platzhalter: {'{project_title}'}
                </p>
              </div>

              <div>
                <Label htmlFor="investment_confirmed">Investition bestätigt</Label>
                <Textarea
                  id="investment_confirmed"
                  value={settings.email_templates?.investment_confirmed || ''}
                  onChange={(e) => updateTemplate('investment_confirmed', e.target.value)}
                  rows={3}
                  data-testid="template-investment-confirmed"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Platzhalter: {'{amount}'}, {'{project_title}'}
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white px-8"
              data-testid="save-settings-btn"
            >
              {saving ? 'Wird gespeichert...' : (
                <>
                  <Save size={20} className="mr-2" />
                  Einstellungen speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
